import moment from "moment";

/**
 * Copy and numbers for the sign-up offer shown when an unauthenticated visitor
 * clicks Book Now.
 *
 * The percentage is hardcoded here on purpose, so it can be changed in one place
 * without touching the component. Note it is a *marketing promise made by the
 * frontend*: the actual saving applied to an order comes from the backend's
 * `discounts` table via `GET /discount/active` (DiscountService::getActiveDiscountData,
 * keyed on `is_active` + `platform`). If that table has no active row for this
 * platform, the cart will not reduce by this amount. Keep the two in step.
 */
export const SIGNUP_DISCOUNT_PERCENT = 10;

/**
 * Digits in the emailed verification code. The backend currently issues 5 (see
 * VerificationModal and `authApi.verifyEmail`), though the approved design shows
 * 6 — change here if the backend moves to 6.
 */
export const VERIFICATION_CODE_LENGTH = 5;

/** Seconds before the "Resend" link becomes active again. */
export const VERIFICATION_RESEND_SECONDS = 30;

/** Shortest password the backend will accept (`min:8`), enforced before sending. */
export const MIN_PASSWORD_LENGTH = 8;

export const signupOfferCopy = {
    headline: `Get up to ${SIGNUP_DISCOUNT_PERCENT}% off this booking`,
    claimButton: "Continue with email",
    namesPromptHeadline: "What should we call you?",
    namesPromptSubtitle:
        "We weren't given your name. Enter your full name to open your rewards account and start earning points on this booking.",
    namesPromptButton: "Save and continue",
    namesPromptSkip: "Continue without earning points",
    detailsHeadline: "What should we call you?",
    detailsSubtitle: "This is the name your booking and your rewards account are held in.",
    detailsButton: "Continue",
    verifyHeadline: "Enter verification code",
    verifySubtitle: (email: string) =>
        `We've sent a ${VERIFICATION_CODE_LENGTH}-digit code to ${email}`,
    loginPrompt: "Already earning points?",
    passwordHeadline: "Create a password",
    passwordSubtitle: "So you can sign back in and find this booking later.",
    passwordButton: "Save and continue",
    passwordHint: `At least ${MIN_PASSWORD_LENGTH} characters`,
    passwordSkip: "Skip for now",
    finishingHeadline: "Getting your booking ready",
    discountAppliedTitle: "Peterpans Rewards discount applied!",
    discountAppliedBody: `You're saving ${SIGNUP_DISCOUNT_PERCENT}% on this booking.`,
};

/**
 * Teaser shown beside the cart total for a signed-in visitor who is not yet
 * earning points.
 *
 * Deliberately aspirational rather than a claim that a discount has been
 * applied: the actual saving comes from points, which are a share of the
 * supplier's commission on that specific product (see DiscountService —
 * commission is whatever TDMS estimates for the order, so it varies per product
 * and per supplier). A fixed "you save X%" would be wrong for most bookings;
 * "up to" is the honest shape.
 *
 * Set to match SIGNUP_DISCOUNT_PERCENT, so the figure a visitor is shown in the
 * cart is the same one the sign-up offer promises. It is a cap rather than a
 * quote: one observed booking came out at 12%, and the real saving varies per
 * product and supplier.
 */
export const CART_TEASER_MAX_PERCENT = 10;

export const cartTeaserCopy = {
    badge: `Up to ${CART_TEASER_MAX_PERCENT}% off!`,
    subtitle: "Here's what you could pay by earning points — not yet applied",
};

/** Characters used for the generated password. Excludes look-alikes (0/O, 1/l/I). */
const PASSWORD_ALPHABET = "abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";

/** Length of the generated password. The backend rule is `min:8`. */
export const GENERATED_PASSWORD_LENGTH = 8;

/**
 * A random password for an account created through the offer sheet, where the
 * visitor is never asked to choose one.
 *
 * Uses crypto.getRandomValues rather than Math.random: this is a real account
 * credential, and Math.random is not a cryptographic source. Falls back only if
 * the API is unavailable.
 */
export const generateSignupPassword = (): string => {
    const length = GENERATED_PASSWORD_LENGTH;
    const alphabet = PASSWORD_ALPHABET;
    const crypto = typeof window !== "undefined" ? window.crypto : undefined;

    if (crypto?.getRandomValues) {
        const bytes = new Uint32Array(length);
        crypto.getRandomValues(bytes);
        return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join("");
    }

    let out = "";
    for (let i = 0; i < length; i += 1) {
        out += alphabet[Math.floor(Math.random() * alphabet.length)];
    }
    return out;
};

/**
 * Banner shown at the top of the checkout while the visitor's profile still holds
 * nothing but the three fields sign-up collected. It stays up until the traveller
 * form replaces the stand-ins, rather than announcing once and vanishing — it is a
 * prompt to finish the step, not a receipt.
 *
 * The body does not promise that saving starts the points earning: sign-up already
 * upgraded the account, so what is actually outstanding is the details themselves.
 */
export const checkoutBannerCopy = {
    title: "Your discount is ready!",
    body: "Add your details below to complete this booking.",
};

/**
 * Stand-in profile values used when upgrading straight after sign-up.
 *
 * The backend gate (checkIfUserContainsLeadCustomerDetail) needs six fields, but
 * the offer sheet only asks for an email address. These fill the gap so the
 * upgrade can run immediately; the traveller form replaces them with real values
 * on first save.
 *
 * The traveller form checks against these exact values to decide which prefilled
 * fields to leave blank, so the ones that can be are deliberately recognisable
 * rather than plausible. The names and the zeroed numbers qualify; the title and
 * country code do not, because "Mr" and "AU" are ordinary real answers — the
 * country code is a real one on purpose, since all of these are forwarded to TDMS
 * when the agent record is created and an invalid code would be rejected.
 */
export const PLACEHOLDER_PROFILE = {
    title: "Mr",
    first_name: "Guest",
    last_name: "Traveller",
    date_of_birth: "01-Jan-1900",
    phone_number: "0000000000",
    post_code: "0000",
    country_code: "AU",
} as const;

/**
 * Five of the seven stand-ins can be recognised again once stored.
 *
 * `title` and `country_code` are the exceptions, left out on purpose: "Mr" and
 * "AU" are both ordinary real answers here, and testing them would blank details
 * a visitor genuinely gave. All seven are written together by the sign-up
 * upgrade, so recognising any one of the other five establishes that the whole
 * set is fake.
 *
 * Each gets its own test because the same value comes back in different shapes
 * depending on which record holds it — the account, a cart redeemer, or an
 * address-book guest.
 */

/** Formats the stored date of birth is known to arrive in. */
const DOB_FORMATS = ["DD-MMM-YYYY", "YYYY-MM-DD", moment.ISO_8601];

export const isPlaceholderDob = (value?: string | null): boolean => {
    if (!value) return false;
    if (value === PLACEHOLDER_PROFILE.date_of_birth) return true;
    // Redeemers and address-book guests are served by different endpoints and
    // spell the same date differently, so match the sentinel date itself rather
    // than one rendering of it.
    const parsed = moment(value, DOB_FORMATS, true);
    return parsed.isValid() && parsed.year() === 1900;
};

export const isPlaceholderPhone = (value?: string | number | null): boolean => {
    if (value === null || value === undefined || value === "") return false;
    if (String(value) === PLACEHOLDER_PROFILE.phone_number) return true;
    // A flat redeemer/suggestion record holds the phone as a number, where
    // Number() has already collapsed the all-zero stand-in to plain 0. An unset
    // phone lands on 0 by that same route and equally needs correcting, so both
    // count as "not a real phone number".
    return typeof value === "number" && value === 0;
};

/**
 * Whether a stored name is the stand-in rather than a real one.
 *
 * Names are free text, so unlike the title and country there is no "ordinary real
 * answer" problem — nobody signing up here is called Guest Traveller — and each
 * half is matched against its own stand-in.
 */
export const isPlaceholderName = (
    field: "first_name" | "last_name",
    value?: string | null,
): boolean => !!value && value.trim() === PLACEHOLDER_PROFILE[field];

export const isPlaceholderPostCode = (value?: string | number | null): boolean =>
    value !== null &&
    value !== undefined &&
    value !== "" &&
    String(value) === PLACEHOLDER_PROFILE.post_code;

/**
 * Anything holding a person's stored details: the signed-in account, a cart
 * redeemer, or an address-book guest. The shapes disagree — `post_code` against
 * `postal_code`, phone as a string or a number — so every variant is accepted
 * and normalised rather than making each caller translate.
 */
export interface IPlaceholderCandidate {
    first_name?: string | null;
    last_name?: string | null;
    date_of_birth?: string | null;
    phone_number?: string | number | null;
    post_code?: string | null;
    postal_code?: string | null;
}

/**
 * Whether a record is still carrying the sign-up stand-ins, and so is waiting on
 * real values before it can be used.
 */
export const hasPlaceholderProfile = (record?: IPlaceholderCandidate | null): boolean =>
    !!record &&
    (isPlaceholderName("first_name", record.first_name) ||
        isPlaceholderName("last_name", record.last_name) ||
        isPlaceholderDob(record.date_of_birth) ||
        isPlaceholderPhone(record.phone_number) ||
        isPlaceholderPostCode(record.post_code ?? record.postal_code));

const isBlank = (value?: string | number | null): boolean =>
    value === null || value === undefined || String(value).trim() === "";

/**
 * Whether a record has only ever been given the one field the offer sheet asks
 * for at sign-up — the email address — with nothing real behind it.
 *
 * This is the signal for dropping a prefilled title or country. Neither can be
 * recognised as a stand-in on its own, because "Mr" and "AU" are ordinary real
 * answers, so the question asked instead is whether the visitor has ever
 * supplied anything beyond sign-up. If the name, date of birth, phone or post
 * code is genuine then they have been through the traveller form at some point,
 * and the title and country are theirs to keep.
 *
 * The email is tested only for presence: it comes straight from the visitor, so
 * unlike everything else it is never a stand-in.
 */
export const hasOnlySignupDetails = (
    record?: (IPlaceholderCandidate & { email?: string | null }) | null,
): boolean => {
    if (!record || isBlank(record.email)) return false;
    const hasRealFirstName =
        !isBlank(record.first_name) && !isPlaceholderName("first_name", record.first_name);
    const hasRealLastName =
        !isBlank(record.last_name) && !isPlaceholderName("last_name", record.last_name);
    const hasRealDob = !isBlank(record.date_of_birth) && !isPlaceholderDob(record.date_of_birth);
    const hasRealPhone = !isBlank(record.phone_number) && !isPlaceholderPhone(record.phone_number);
    const postCode = record.post_code ?? record.postal_code;
    const hasRealPostCode = !isBlank(postCode) && !isPlaceholderPostCode(postCode);
    return (
        !hasRealFirstName && !hasRealLastName && !hasRealDob && !hasRealPhone && !hasRealPostCode
    );
};
